'use client';

import { LayoutPanelTopIcon, TableIcon } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

type LayoutSwitcherProps = {
  mode: 'kanban' | 'table';
  onModeChange: (mode: 'kanban' | 'table') => void;
  className?: string;
  disabled?: boolean;
};

const LayoutSwitcher = ({ mode, onModeChange, className, disabled = false }: LayoutSwitcherProps) => {
  const isTable = mode === 'table';
  const containerRef = useRef<HTMLLabelElement>(null);
  const kanbanRef = useRef<HTMLSpanElement>(null);
  const tableRef = useRef<HTMLSpanElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState<{ width: number; left: number }>({ width: 0, left: 0 });

  const handleToggle = () => {
    if (disabled) {
      return;
    }
    onModeChange(isTable ? 'kanban' : 'table');
  };

  const updateIndicator = useCallback(() => {
    const container = containerRef.current;
    const activeEl = isTable ? tableRef.current : kanbanRef.current;

    if (!container || !activeEl) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const activeRect = activeEl.getBoundingClientRect();

    setIndicatorStyle({
      width: activeRect.width,
      left: activeRect.left - containerRect.left,
    });
  }, [isTable]);

  useLayoutEffect(() => {
    updateIndicator();
  }, [updateIndicator]);

  useEffect(() => {
    const handleResize = () => {
      updateIndicator();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [updateIndicator]);

  return (
    <label
      ref={containerRef}
      className={cn(
        'themeSwitcherTwo shadow-card relative inline-flex h-10 select-none items-stretch justify-center rounded-md inset-shadow-sm bg-primary-bg p-1',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        className,
      )}
      aria-disabled={disabled}
    >
      <input
        type="checkbox"
        className="sr-only"
        checked={isTable}
        onChange={handleToggle}
        disabled={disabled}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute top-1 bottom-1 z-0 rounded-lg bg-primary transition-all duration-200 ease-out"
        style={{
          left: 0,
          width: `${indicatorStyle.width}px`,
          transform: `translateX(${indicatorStyle.left}px)`,
        }}
      />
      <span
        ref={kanbanRef}
        className={cn(
          'relative z-[1] flex h-full items-center gap-[6px] rounded px-[18px] text-sm font-medium transition-colors',
          !isTable ? 'text-white' : 'text-body-color',
        )}
      >
        <LayoutPanelTopIcon className="h-4 w-4" />
        Kanban
      </span>
      <span
        ref={tableRef}
        className={cn(
          'relative z-[1] flex h-full items-center gap-[6px] rounded px-[18px] text-sm font-medium transition-colors',
          isTable ? 'text-white' : 'text-body-color',
        )}
      >
        <TableIcon className="h-4 w-4" />
        Tabel
      </span>
    </label>
  );
};

export default LayoutSwitcher;
